import { useState, useEffect, useCallback, useRef } from 'react';
import { LaunchesService } from '../services/launchesApi';
import type { LaunchBoard, LaunchBoardUnit } from '../types';

const POLL_INTERVAL_MS = 15_000;

interface UseLaunchBoardReturn {
  board: LaunchBoard | null;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
  selectedUnit: LaunchBoardUnit | null;
  selectUnit: (unit: LaunchBoardUnit | null) => void;
}

/**
 * Fetches the board snapshot and polls every 15s to reflect hold expirations.
 */
export function useLaunchBoard(agencyId: string, launchId: string): UseLaunchBoardReturn {
  const [board, setBoard] = useState<LaunchBoard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<LaunchBoardUnit | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBoard = useCallback(() => {
    if (!agencyId || !launchId) return;
    setIsError(false);
    LaunchesService.board(agencyId, launchId)
      .then((data) => {
        setBoard(data);
        setIsLoading(false);
        if (selectedUnit) {
          const updated = data.units.find((u) => u.unit_id === selectedUnit.unit_id);
          setSelectedUnit(updated ?? null);
        }
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  }, [agencyId, launchId, selectedUnit]);

  useEffect(() => {
    setIsLoading(true);
    fetchBoard();
    intervalRef.current = setInterval(fetchBoard, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [agencyId, launchId]);

  const selectUnit = useCallback((unit: LaunchBoardUnit | null) => {
    setSelectedUnit(unit);
  }, []);

  return { board, isLoading, isError, retry: fetchBoard, selectedUnit, selectUnit };
}
